import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { NatureResultData } from 'routes/HumanResourcesModule/business/types';
import HrCard from 'routes/HumanResourcesModule/components/HrCard';

import {
    GridApi,
    GridReadyEvent,
    ProcessRowGroupForExportParams,
} from 'ag-grid-community';
import LoadingData from 'routes/HumanResourcesModule/components/LoadingGrid';
import { StyNoSpacingGridContainer } from 'routes/HumanResourcesModule/components/ui/StyGridContainer';
import { AgGridReactProps } from 'ag-grid-react';
import NoData from 'routes/HumanResourcesModule/components/HrDataGrid/NoData';
import HelperAlert from 'ui/HelperAlert/HelperAlert';
import {
    autoGroupColumnDef,
    getNatureColDefs,
} from '../helpers/colDefinitions';
import ExportButton from 'components/ExportButton/ExportButton';
import { snakify } from 'helpers/syntaxHelper';
import {
    TRACKABLE_EVENTS,
    TRACKABLE_EVENTS_METADATA,
    trackEventPRH,
} from 'helpers/trackerHelpers';
import { Box } from '@mui/system';
import { Link } from '@mui/material';
import { ACCOUNTABLE_STANDARD_CODE } from 'routes/HumanResourcesModule/business/definitions';
import { useGetAllHrNatures } from '../helpers/hooks/natures';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { useTranslation } from 'react-i18next';
import { NatureVentilationType } from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/helpers/hrVizInfos';
import FullScreenProvider from 'contexts/FullScreenContextProvider';
import ConditionnalZoomIconButton from 'components/ConditionnalZoomIconButton';
import { HUMAN_RESOURCES } from 'business/constants';
import AgGridReact from 'ui/AgGridReact/AgGridReact';

type Props = {
    hrSimulation: HrSimulation;
    resultSimulationData: NatureResultData[];
    isLoading: boolean;
    titleNode: React.ReactNode;
    ventilationType: NatureVentilationType;
};

const DOC_LINK =
    'https://docs.manty.eu/visualisation-du-r%C3%A9sultat-de-la-simulation-par-nature-comptable?hs_preview=ggsVRxQa-173007345303';

const NatureResultDataGrid = ({
    resultSimulationData,
    isLoading,
    titleNode,
    hrSimulation,
    ventilationType,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const noService = t('humanResources.simulation.resultByNature.noService');
    const [isFullScreen, setIsFullScreen] = useState(false);

    const [gridApi, setGridApi] = React.useState<GridApi | null>(null);
    const { data: hrNatures = [] } = useGetAllHrNatures();
    const columnDefs = getNatureColDefs({
        hrNatures,
        hrSimulation,
        ventilationType,
    });
    const getDataPath = useCallback(
        (data: NatureResultData) => {
            let dataPath = [];
            const natureCode = data[ACCOUNTABLE_STANDARD_CODE];
            if (!natureCode) {
                dataPath = [t('humanResources.simulation.otherNature')];
            }
            if (natureCode.length < 2) {
                dataPath = [natureCode];
            }
            dataPath = [
                ...Array.from(
                    new Set(
                        natureCode
                            .split('')
                            .map((_, i) => natureCode.substring(0, i + 2)),
                    ),
                ),
            ];
            if (ventilationType === NatureVentilationType.SERVICE_AND_NATURE) {
                dataPath = [data.orgChartHrName ?? noService, ...dataPath];
            }
            return dataPath;
        },
        [ventilationType],
    );
    const resizeColumns = useCallback(() => {
        // this hack is a bit flaky, in very large screen 8 columns  might not take the grid's entire width
        // a better handling should be applied
        if (gridApi) {
            gridApi.sizeColumnsToFit();
        }
    }, [gridApi]);

    const onGridReady = useCallback((event: GridReadyEvent) => {
        setGridApi(event.api);
        resizeColumns();
    }, []);

    const onFirstDataRendered = () => {
        resizeColumns();
    };

    const defaultGridProps: AgGridReactProps = {
        className: 'ag-theme-material',
        pagination: true,
        paginationPageSize: 10,
        rowHeight: 32,
        headerHeight: 36,
    };

    useEffect(() => {
        if (gridApi && columnDefs) {
            // in case there is just only 1 month to display, the column is resized
            // to fill the remaning available space
            // the default configuration provides 4 columns when 1 month is displayed
            resizeColumns();
        }
    }, [columnDefs]);

    const exportFileName = t(
        'humanResources.simulation.simulationResultDataFileName',
        {
            title: snakify(hrSimulation.title),
        },
    );

    const exportOptions = {
        skipRowGroups: false,
        allColumns: true,
        fileName: exportFileName,
        processRowGroupCallback: (params: ProcessRowGroupForExportParams) =>
            params.node.key ?? '',
    };

    const headerActions = (
        <ExportButton
            exportOptions={exportOptions}
            gridApi={gridApi}
            trackEvent={() =>
                trackEventPRH(
                    TRACKABLE_EVENTS.human_resources.simulation
                        .exportAccountablePdf,
                )
            }
        />
    );

    const content = useMemo(() => {
        if (isLoading) {
            return <LoadingData />;
        }
        if (resultSimulationData.length === 0) {
            return <NoData />;
        }
        return (
            <StyNoSpacingGridContainer
                sx={{
                    height: '100%',
                    width: '100%',
                    '&&&  .ag-header-group-cell-with-group, .ag-header-cell': {
                        borderLeft: 'none !important',
                    },
                    '&&& .ag-cell': {
                        borderRight: 'none !important',
                    },
                }}
            >
                <AgGridReact
                    treeData
                    domLayout={isFullScreen ? 'normal' : 'autoHeight'}
                    getDataPath={getDataPath}
                    {...defaultGridProps}
                    pagination={!isFullScreen}
                    enableBrowserTooltips
                    tooltipShowDelay={0}
                    rowData={resultSimulationData}
                    columnDefs={columnDefs}
                    autoGroupColumnDef={autoGroupColumnDef(ventilationType)}
                    onGridReady={onGridReady}
                    onFirstDataRendered={onFirstDataRendered}
                />
            </StyNoSpacingGridContainer>
        );
    }, [
        isLoading,
        resultSimulationData,
        defaultGridProps,
        onFirstDataRendered,
        columnDefs,
        isFullScreen,
        onGridReady,
    ]);

    return (
        <FullScreenProvider
            module={HUMAN_RESOURCES}
            trackEventMetadata={{
                gridName:
                    TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                        .resultNatureGrid,
            }}
        >
            <HrCard
                title="titre"
                titleNode={titleNode}
                cardHeaderActions={headerActions}
                startAdornment={<ConditionnalZoomIconButton />}
                setIsFullScreen={setIsFullScreen}
            >
                {content}
                <HelperAlert>
                    <Box>
                        <Link href={DOC_LINK} target="_blank">
                            {t('humanResources.settings.helpLink')}
                        </Link>{' '}
                        <span>{t('humanResources.simulation.helperText')}</span>
                    </Box>
                </HelperAlert>
            </HrCard>
        </FullScreenProvider>
    );
};

export default memo(NatureResultDataGrid);
